import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { PaymentMethods, PaymentMethodsChangeEvent } from 'src/components/PaymentMethods';
import { TabularFigures } from './TabularFigures.styles';
import { formatSignupNumber } from '../../helpers/phoneNumber';
import { SignupHoldRecovery, SignupPaymentPhase } from '../../controllers/SignupPaymentViewController';
import { noticeSx } from '../../helpers/sx';

type Props = {
  companyId: string;
  selectedNumber: string | null;
  /** Whether the hold on that number is still live. A lapsed one is taken again by the checkout. */
  numberHeld: boolean;
  /** When it runs out, so the screen can count down rather than repeat what was true when it opened. */
  numberHeldUntil: string | null;
  /** What became of a hold that had already lapsed when the screen opened. */
  holdRecovery: SignupHoldRecovery;
  /** Registration was refused and somebody has been told: from here the client waits to be contacted. */
  handedOver: boolean;
  phase: SignupPaymentPhase;
  busy: boolean;
  canRetry: boolean;
  error: string | null;
  onPay: () => void;
  onRetry: () => void;
  /**
   * The refusal on screen has stopped describing what will happen next. Called when the client starts
   * entering another card, and when the card that would be charged actually changes.
   */
  onRefusalStale: () => void;
  onChooseAnotherNumber: () => void;
};

/**
 * What the client is told while the charge is out of their hands. A refusal is reported by Stripe and reaches
 * the funnel, so the screen never has to infer one from silence: blaming the card after ninety seconds is a
 * lie whenever the money has arrived and the number is already being ordered.
 */
const PHASE_NOTICE: Partial<Record<SignupPaymentPhase, { severity: 'info' | 'warning'; text: string }>> = {
  charging: {
    severity: 'info',
    text: 'Taking the payment. This can take up to a minute — please keep this page open.',
  },
  finalizing: {
    severity: 'info',
    text: 'Payment accepted. We are setting up your number now — this usually takes a few moments.',
  },
  declined: {
    severity: 'warning',
    text: 'Your card was declined and nothing was charged. Add another card and try the payment again.',
  },
  slow: {
    severity: 'info',
    text: 'This is taking longer than usual. You can leave this page open or close it — we will email you as soon as your number is ready.',
  },
  // Says outright that nothing was charged: this is the one long wait where the client is not waiting on
  // money that has already left their account, and the button below is what puts it right.
  stalled: {
    severity: 'warning',
    text: 'We could not take the payment and nothing was charged. Your number is still held for you — try the payment again.',
  },
};

const remainingUntil = (until: string | null): number | null => {
  if (until == null) return null;
  const left = new Date(until).getTime() - Date.now();
  return Number.isNaN(left) ? null : Math.max(0, left);
};

const asMinutesAndSeconds = (remainingMs: number): string => {
  const seconds = Math.ceil(remainingMs / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * Figures of one width: a proportional 1 is half a 0, so seconds counted down in the middle of a sentence
 * would take the rest of it walking about once a second. No reserved width beyond that — the sentence moves
 * once, on the step from ten minutes to nine, and a permanent gap after the clock costs more than that.
 */
const Countdown: React.FC<{ remainingMs: number }> = ({ remainingMs }) => (
  <TabularFigures component="span">{asMinutesAndSeconds(remainingMs)}</TabularFigures>
);

/**
 * The hold as a clock rather than as an answer read once. A card takes minutes to type and the funnel is
 * polled only while a charge is in flight, so a screen left standing would go on promising a hold that ran
 * out under it — which is the one thing this screen must not do.
 */
const useHoldCountdown = (numberHeldUntil: string | null): number | null => {
  const [remainingMs, setRemainingMs] = useState(() => remainingUntil(numberHeldUntil));

  useEffect(() => {
    setRemainingMs(remainingUntil(numberHeldUntil));
    if (numberHeldUntil == null) return;

    const timer = setInterval(() => setRemainingMs(remainingUntil(numberHeldUntil)), 1000);
    return () => clearInterval(timer);
  }, [numberHeldUntil]);

  return remainingMs;
};

export const SignupPaymentView: React.FC<Props> = ({
  companyId,
  selectedNumber,
  numberHeld,
  numberHeldUntil,
  holdRecovery,
  handedOver,
  phase,
  busy,
  canRetry,
  error,
  onPay,
  onRetry,
  onRefusalStale,
  onChooseAnotherNumber,
}) => {
  /**
   * How many cards are on file, null until the shared component has said. It reports on every change and once
   * on load, so a client who already has one — from an earlier attempt, or from the wizard — can pay straight
   * away instead of being asked for it a second time. The count rather than a flag because one card and
   * several are answered differently below.
   */
  const [cardCount, setCardCount] = useState<number | null>(null);
  /**
   * The card the charge will actually go to, named only where that is a question: Stripe charges the
   * customer's default, not the card added last, so with several on file the client has no way of telling
   * which. With one there is nothing to disambiguate, and with a card being typed in the answer is whatever
   * is in the form — not this, which still holds the card the form is replacing.
   */
  const [chargedCard, setChargedCard] = useState<string | null>(null);
  /**
   * A card is being typed in. The form carries the paying button while it is open — that is the whole point
   * of one button instead of two — so the screen's own must stand down rather than offer the same charge
   * twice over.
   */
  const [enteringCard, setEnteringCard] = useState(false);

  const handleCardsChanged = (event: PaymentMethodsChangeEvent) => {
    setCardCount(event.methods.length);
    if (event.reason === 'added') setEnteringCard(false);

    const paying = event.methods.find(method => method.isDefault) ?? event.methods[0];
    setChargedCard(
      paying?.card == null
        ? null
        : `${paying.card.brand.toUpperCase()} ····${paying.card.last4}`,
    );

    /**
     * A card added, or another one made the default, is the client changing what the next charge will go to
     * — which is exactly what the refusal on screen no longer describes. Not on `loaded`: opening the screen
     * after a refused charge is when that message is the truth and worth reading.
     */
    if (event.reason === 'added' || event.reason === 'default') {
      onRefusalStale();
    }
  };

  const notice = PHASE_NOTICE[phase] ?? null;

  /**
   * The server's word for the hold, kept honest by the clock: it may well run out while this screen is open,
   * and nothing polls the funnel until a charge is asked for.
   *
   * Running out here is not answered with another reservation, unlike a hold already lapsed on arrival: the
   * charge takes the number again by itself, and a screen left open would reserve on a timer for as long as
   * the tab lives.
   */
  const remainingHoldMs = useHoldCountdown(numberHeldUntil);
  const held = numberHeld && (remainingHoldMs == null || remainingHoldMs > 0);

  // The number as the client reads it, or nothing while the funnel is still being read. What goes to the
  // API is the number as Telnyx spells it.
  const shownNumber = selectedNumber == null ? null : formatSignupNumber(selectedNumber);

  /**
   * The number as an aside to a sentence that reads without it — the funnel may still be loading. Written
   * once and shared by all four readings of the hold, which is what keeps them from drifting apart in
   * weight or punctuation.
   */
  const numberAside = shownNumber == null ? null : (
    <>
      <Box component="span" fontWeight="fontWeightSemiBold" color="text.primary">
        {`(${shownNumber})`}
      </Box>
      {' '}
    </>
  );

  /**
   * One action under two names. `canRetry` means an invoice is already open, and paying it again is a retry
   * rather than a fresh charge — the funnel refuses the wrong one of the two.
   */
  const action = canRetry
    ? { label: 'Try the payment again', run: onRetry }
    : { label: 'Pay and get my number', run: onPay };

  /**
   * The number is gone, or the screen is still finding out. Both hold the card block back — one for a moment,
   * the other for good — because a card entered against a number nobody has any more pays for nothing.
   */
  const numberLost = holdRecovery === 'lost';
  const reclaiming = holdRecovery === 'reclaiming';

  // Nothing on the screen is the client's to move: neither the button nor the cards the charge is already
  // going to. `slow` is in and `stalled` is out — the retry `stalled` offers is worth a different card.
  const inFlight = busy || phase === 'slow';

  /*
    The money is in, the number is bought, and the registry refused the brand — so nothing on this screen is
    the client's to act on any more, the card block included: it would offer to pay for what is already paid.
  */
  if (handedOver) {
    return (
      <Box>
        <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
          We are finishing your registration
        </Typography>
        <Alert severity="warning">
          Your payment went through and your number is yours. The carriers had a question about your company
          details, so somebody from our team will be in touch — there is nothing else for you to do here. Try
          refreshing the page later to check the updated status, in case we were able to resolve the issue
          automatically.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
        Add Payment Details
      </Typography>
      {/*
        Naming the number is the whole point of holding it: this is the moment money is asked for. And the
        window is described as it stands — a client who took longer than it allowed is told so, rather than
        promised a hold they can no longer see and finding out from the charge.
      */}
      <Typography variant="body1" color="text.secondary" mb={numberLost ? 1 : 4}>
        {'Your phone number '}
        {numberAside}
        {numberLost && 'has been taken by somebody else while you were away.'}
        {reclaiming && 'lapsed while you were away — taking it again…'}
        {!numberLost && !reclaiming && (held ? (
          <>
            {'is being held for you for the next '}
            {remainingHoldMs != null ? <Countdown remainingMs={remainingHoldMs} /> : 'few minutes'}
            {'. Add payment information to secure the number and start your plan.'}
          </>
        ) : (
          'is no longer held and may be bought by somebody else. Add payment information to try to '
          + 'secure it and start your plan.'
        ))}
      </Typography>

      {/* Said outright: the window ran out and the number was taken again, so the minutes above are new. */}
      {holdRecovery === 'reclaimed' && held && (
        <Typography variant="body2" color="text.secondary" mb={4}>
          It had lapsed while you were away, and we have just reserved it again.
        </Typography>
      )}

      {/*
        No card is asked for a number that is gone: whatever is entered here would pay for something else, and
        which number that is has to be answered first.
      */}
      {numberLost && (
        <Box mt={4}>
          <Alert severity="warning" sx={noticeSx}>
            Your reservation has ended and this number has since gone to somebody else. Nothing has been
            charged — pick another number and carry on from there.
          </Alert>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" size="large" onClick={onChooseAnotherNumber}>
              Choose another number
            </Button>
          </Box>
        </Box>
      )}

      {/*
        Kept mounted but out of sight while the number is being taken again: the card block asks Stripe for its
        own secrets as it mounts, and unmounting it for the second that takes would pay for that twice. Gone
        altogether when the number is lost — there is nothing left for it to pay for.
      */}
      {!numberLost && (
        <Box hidden={reclaiming}>
          <Stack spacing={3}>
            {/*
              The card form pays with its own button rather than saving the card and leaving the client to press
              a second one: nothing happens between the two, and the first of them asks for a decision the client
              has already made by typing the card in.
            */}
            <PaymentMethods
              companyId={companyId}
              onChanged={handleCardsChanged}
              makeNewCardDefault
              onAddCardStarted={() => {
                setEnteringCard(true);
                onRefusalStale();
              }}
              // Closed without a card: the screen's own button is the one that pays again.
              onAddCardCancelled={() => setEnteringCard(false)}
              locked={inFlight}
              submit={{ label: action.label, busyLabel: 'Paying…', onSubmitted: action.run }}
            />

            {chargedCard != null && cardCount != null && cardCount > 1 && !enteringCard && !inFlight && (
              <Typography variant="body2" color="text.secondary">
                {`We will charge ${chargedCard}.`}
              </Typography>
            )}

            {error != null && <Alert severity="error">{error}</Alert>}

            {notice != null && (
              <Alert severity={notice.severity} icon={busy ? <CircularProgress size={20} /> : undefined}>
                {notice.text}
              </Alert>
            )}

            {/*
              Only for a card that is already on file — from an earlier attempt, or from the wizard. With none
              there, or another one being typed in, the form's own button is the one that pays.

              And only while there is something to press: a charge on its way ends in a screen of its own, so
              a greyed-out button under the notice offers an action that will never be taken.
            */}
            {cardCount != null && cardCount > 0 && !enteringCard && !inFlight && (
              <Box display="flex" justifyContent="flex-end">
                <Button variant="contained" size="large" onClick={action.run} data-testid="signup-pay">
                  {action.label}
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
